import React, { ChangeEvent, useEffect, useRef } from 'react';
import styles from './edit-cell.module.scss';

type Props = {
    value: string;
    onChange: (value: string, blur: boolean) => void;
    active: boolean;
    placeholder?: string;
};

const TextEditCell = ({ value: propValue, onChange, active, placeholder }: Props) => {
    const ref = useRef<HTMLTextAreaElement>(null);
    const wasActiveRef = useRef(false);
    const [draft, setDraft] = React.useState(propValue);

    useEffect(() => {
        if (!active) {
            setDraft(propValue);
        }
    }, [propValue, active]);

    const commit = () => {
        if (draft !== propValue) {
            onChange(draft, true);
        }
    };

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setDraft(e.target.value);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            e.preventDefault();
            commit();
            ref.current?.blur();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(propValue);
            ref.current?.blur();
        }
    };

    const handleBlur: React.FocusEventHandler<HTMLTextAreaElement> = () => {
        commit();
    };

    useEffect(() => {
        if (!active) {
            ref.current?.blur();
        } else if (!wasActiveRef.current && draft) {
            ref.current?.setSelectionRange(draft.length, draft.length);
        }

        wasActiveRef.current = active;
    }, [active, draft]);

    return (
        <textarea
            autoFocus
            ref={ref}
            placeholder={placeholder}
            className={styles.text}
            value={active ? draft : propValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
        />
    );
};

export default TextEditCell;
